/**
 * S16 — Audit Log (`/settings/audit`).
 *
 * Global, tamper-evident governance ledger. Every ALGOS action
 * (workflow approvals, policy publishes, framework evidence uploads,
 * agent updates, drift detections) is hash-chained backwards to the
 * genesis entry. The page surfaces:
 *
 *   · KPI strip — total events, last 24h, distinct actors, chain status
 *   · Chain integrity strip — animated "Verify chain" CTA
 *   · Filters — entity type, action, actor, date, severity, free-text
 *   · Event table with cross-link drill-down (target → corresponding page)
 *   · Right-side drawer showing full entry, hash chain neighbourhood
 *
 * Read-only. Export and live-stream are deferred.
 */
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, ShieldCheck, Activity, UsersRound, Clock, RefreshCw, ArrowUpRight, Plus,
  CheckCircle2, AlertTriangle, X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntityList } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  EntityChip, SeverityPill, HashBadge, ChainStatusDot,
} from "./components/AuditPrimitives";
import AuditEventDrawer from "./AuditEventDrawer";
import ChainWalkOverlay from "./ChainWalkOverlay";
import CsvExportButton from "@/components/data/CsvExportButton";
import { csvFilename } from "@/lib/csvExport";

const AUDIT_CSV_COLUMNS = [
  { header: "Entry ID",  accessor: (e) => e.id },
  { header: "Timestamp", accessor: (e) => e.ts || "" },
  { header: "Actor",     accessor: (e) => e.actor || "" },
  { header: "Action",    accessor: (e) => e.action || "" },
  { header: "Entity",    accessor: (e) => e.entity || "" },
  { header: "Target",    accessor: (e) => e.target || "" },
  { header: "Severity",  accessor: (e) => e.severity || "" },
  { header: "Narrative", accessor: (e) => e.narrative || "" },
  { header: "Hash",      accessor: (e) => e.hash || "" },
  { header: "Prev hash", accessor: (e) => e.prevHash || "" },
];

// An audit `action` value is treated as a narrative sentence when it
// looks like prose (has spaces and is longer than a verb code).
function looksLikeSentence(s) {
  return typeof s === "string" && s.length > 24 && s.includes(" ");
}
import { resolveAuditTargetRoute } from "./auditTargets";

const ENTITY_OPTIONS = [
  { value: "", label: "All entity types" },
  { value: "request", label: "Deployment requests" },
  { value: "policy", label: "Policies" },
  { value: "agent", label: "Agents" },
  { value: "opportunity", label: "Opportunities" },
  { value: "incident", label: "Incidents" },
  { value: "framework", label: "Frameworks" },
  { value: "vendor", label: "Vendors" },
  { value: "report", label: "Reports" },
  { value: "system", label: "System" },
];

const SEVERITY_OPTIONS = [
  { value: "", label: "Any severity" },
  { value: "low", label: "Low" },
  { value: "med", label: "Medium" },
  { value: "high", label: "High" },
];

const DATE_OPTIONS = [
  { value: "", label: "All dates" },
  { value: "24h", label: "Last 24h" },
  { value: "7d",  label: "Last 7 days" },
  { value: "14d", label: "Last 14 days" },
];

export default function AuditLog() {
  const { user } = useAuth();
  // Phase 3b — read canonical entries from /api/audit, then layer the
  // overlay so self-audit events appended during this session show up
  // immediately. recordAuditChainVerify still updates the overlay for
  // optimistic refresh; refetch is also wired so the canonical list
  // catches up.
  const { items: serverEntries, loading, error, lastSyncedAt, refetch } = useEntityList("/api/audit");
  const { isCustomer, recordAuditChainVerify, pendingEntities } = useTenantData();
  const auditEntries = useMemo(() => {
    const pending = pendingEntities?.auditEntries || [];
    if (pending.length === 0) return serverEntries;
    const pendingIds = new Set(pending.map((e) => e.id));
    return [...pending, ...serverEntries.filter((e) => !pendingIds.has(e.id))];
  }, [serverEntries, pendingEntities]);
  const navigate = useNavigate();
  const canVerifyChain = (user?.permissions || []).includes("verify:audit-chain");
  const [search, setSearch] = useState("");
  const [entity, setEntity] = useState("");
  const [severity, setSeverity] = useState("");
  const [actor, setActor] = useState("");
  const [date, setDate] = useState("");
  const [chainStatus, setChainStatus] = useState("idle");
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyError, setVerifyError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [chainWalkOpen, setChainWalkOpen] = useState(false);

  const actors = useMemo(
    () => Array.from(new Set(auditEntries.map((e) => e.actor))).sort(),
    [auditEntries],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return auditEntries.filter((e) => filterEntry(e, { q, entity, severity, actor, date }));
  }, [auditEntries, search, entity, severity, actor, date]);

  const summary = useMemo(() => deriveSummary(auditEntries), [auditEntries]);

  const selected = selectedId
    ? auditEntries.find((e) => e.id === selectedId) || null
    : null;

  const reset = useCallback(() => {
    setSearch(""); setEntity(""); setSeverity(""); setActor(""); setDate("");
  }, []);

  const verifyChain = useCallback(async () => {
    setChainStatus("verifying");
    setVerifyError(null);
    setChainWalkOpen(true);
    // Floor the perceived runtime so the chain-walk animation has time to
    // play out — the demo punch comes from the visual journey, not the
    // (often sub-100ms) server reply.
    const minRuntimeMs = 2800;
    const started = performance.now();
    try {
      const result = await api.post("/api/audit/verify");
      const elapsed = performance.now() - started;
      if (elapsed < minRuntimeMs) {
        await new Promise((r) => setTimeout(r, minRuntimeMs - elapsed));
      }
      setVerifyResult(result);
      setChainStatus(result.valid ? "verified" : "broken");
      if (result.auditEntry) recordAuditChainVerify(result.auditEntry);
    } catch (e) {
      const msg = e instanceof ApiError
        ? `${e.status}: ${e.message}`
        : "Verification failed — please try again.";
      setVerifyError(msg);
      setChainStatus("error");
    }
  }, [recordAuditChainVerify]);

  const dismissVerifyResult = useCallback(() => {
    setVerifyResult(null);
    setVerifyError(null);
  }, []);

  const openEntry = useCallback((e) => setSelectedId(e.id), []);
  const closeEntry = useCallback(() => setSelectedId(null), []);

  const navigateToTarget = useCallback(
    (e) => {
      const route = resolveAuditTargetRoute(e);
      if (route) navigate(route);
    },
    [navigate],
  );

  if (isCustomer && auditEntries.length === 0) {
    return <CustomerEmpty userName={user?.name?.split(" ")[0]} onSubmit={() => navigate("/govern/agents")} />;
  }

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="audit-log">
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.05} />
      </div>

      <Header />
      <KpiStrip summary={summary} chainStatus={chainStatus} />

      <ChainStrip
        chainStatus={chainStatus}
        onVerify={verifyChain}
        entries={auditEntries}
        canVerify={canVerifyChain}
      />

      <div className="mt-4 surface-card shadow-e1 overflow-hidden" data-testid="audit-panel">
        <Toolbar
          search={search} setSearch={setSearch}
          entity={entity} setEntity={setEntity}
          severity={severity} setSeverity={setSeverity}
          actor={actor} setActor={setActor}
          actors={actors}
          date={date} setDate={setDate}
          matchCount={filtered.length}
          totalCount={auditEntries.length}
          onReset={reset}
          csvExport={
            <CsvExportButton
              rows={filtered}
              columns={AUDIT_CSV_COLUMNS}
              filename={csvFilename("audit-log", user?.tenantId)}
              testId="audit-csv-export"
            />
          }
        />
        <EventTable items={filtered} onSelect={openEntry} onTarget={navigateToTarget} />
      </div>

      <AuditEventDrawer
        entry={selected}
        entries={auditEntries}
        onClose={closeEntry}
        onTargetClick={navigateToTarget}
      />

      <VerifyResultModal
        result={verifyResult}
        error={verifyError}
        onClose={dismissVerifyResult}
      />

      <ChainWalkOverlay
        open={chainWalkOpen}
        onClose={() => { setChainWalkOpen(false); dismissVerifyResult(); }}
        entries={auditEntries}
        status={chainStatus}
        verifyResult={verifyResult}
        verifyError={verifyError}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function Header() {
  return (
    <div className="relative">
      <p className="eyebrow">Settings · Audit Log</p>
      <h1 className="text-display-md text-ink-primary mt-1">Audit Log</h1>
      <p className="text-ink-secondary mt-1 max-w-2xl">
        Every governed action — approvals, policy publishes, framework
        evidence uploads, drift detections — is hash-chained into a
        tamper-evident ledger. Open any event to inspect its
        cryptographic neighbourhood.
      </p>
    </div>
  );
}

function KpiStrip({ summary, chainStatus }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="audit-kpis">
      <KpiTile
        label="Total events"
        value={summary.total.toString()}
        delta={`${summary.high} high-severity`}
        deltaTone={summary.high > 0 ? "down" : "up"}
        hint="hash-chained · immutable"
        icon={Activity}
        tone="tralenex"
        testId="audit-kpi-total"
      />
      <KpiTile
        label="Last 24h"
        value={summary.last24h.toString()}
        delta="active ledger"
        deltaTone="up"
        hint="events recorded"
        icon={Clock}
        tone="opp"
        testId="audit-kpi-24h"
      />
      <KpiTile
        label="Distinct actors"
        value={summary.actorCount.toString()}
        delta={`${summary.systemEvents} system events`}
        deltaTone="neutral"
        hint="humans + service identities"
        icon={UsersRound}
        tone="gold"
        testId="audit-kpi-actors"
      />
      <KpiTile
        label="Chain integrity"
        value={chainKpiValue(chainStatus)}
        delta={chainKpiDelta(chainStatus)}
        deltaTone={chainKpiTone(chainStatus)}
        hint={chainStatus === "idle" ? "press Verify to recompute" : "last verification result"}
        icon={ShieldCheck}
        tone={chainStatus === "verified" ? "tralenex" : chainStatus === "broken" || chainStatus === "error" ? "risk" : "gold"}
        testId="audit-kpi-chain"
      />
    </div>
  );
}

function ChainStrip({ chainStatus, onVerify, entries, canVerify }) {
  const head = entries[0];
  const tail = entries[entries.length - 1];
  const disabled = chainStatus === "verifying" || !canVerify;
  return (
    <div className="mt-6 surface-card shadow-e1 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="audit-chain-strip">
      <ChainStatusDot status={chainStatus === "idle" ? "verified" : chainStatus} />
      <span className="text-meta text-ink-secondary">
        {entries.length} entries · genesis → head
      </span>
      <span className="flex-1" />
      {tail && (
        <HashBadge label="genesis" value={tail.prevHash} testId="audit-chain-genesis-hash" />
      )}
      <span className="text-ink-disabled">→</span>
      {head && (
        <HashBadge label="head" value={head.hash} testId="audit-chain-head-hash" />
      )}
      <button
        type="button"
        onClick={onVerify}
        disabled={disabled}
        title={canVerify ? "" : "You lack the verify:audit-chain permission for this tenant."}
        className={cn(
          "inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-sm font-semibold transition-colors",
          chainStatus === "verifying"
            ? "bg-inset text-ink-disabled cursor-wait"
            : !canVerify
              ? "bg-inset text-ink-disabled cursor-not-allowed"
              : chainStatus === "broken" || chainStatus === "error"
                ? "bg-risk-high text-white hover:bg-risk-high/90"
                : "bg-tralenex-600 text-white hover:bg-tralenex-700",
        )}
        data-testid="audit-chain-verify"
      >
        <RefreshCw size={13} className={chainStatus === "verifying" ? "animate-spin" : ""} />
        {chainStatus === "verifying" ? "Verifying…" : chainStatus === "verified" ? "Re-verify chain" : "Verify chain"}
      </button>
    </div>
  );
}

function Toolbar(props) {
  const {
    search, setSearch, entity, setEntity, severity, setSeverity,
    actor, setActor, actors, date, setDate,
    matchCount, totalCount, onReset, csvExport,
  } = props;
  return (
    <div className="px-4 pt-3 pb-3 border-b border-border-subtle flex flex-wrap items-center gap-2" data-testid="audit-toolbar">
      <div className="relative flex-1 min-w-[240px]">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-disabled" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search action, actor, target, hash…"
          className="w-full pl-8 pr-3 h-8 text-sm rounded-md border border-border-subtle bg-white focus:outline-none focus:border-tralenex-400"
          data-testid="audit-search-input"
        />
      </div>
      <Select value={entity} onChange={setEntity} testId="audit-filter-entity" options={ENTITY_OPTIONS} />
      <Select value={severity} onChange={setSeverity} testId="audit-filter-severity" options={SEVERITY_OPTIONS} />
      <select
        value={actor}
        onChange={(e) => setActor(e.target.value)}
        data-testid="audit-filter-actor"
        className="h-8 px-2 text-sm rounded-md border border-border-subtle bg-white focus:outline-none focus:border-tralenex-400"
      >
        <option value="">All actors</option>
        {actors.map((a) => <option key={a} value={a}>{a}</option>)}
      </select>
      <Select value={date} onChange={setDate} testId="audit-filter-date" options={DATE_OPTIONS} />
      <button
        type="button"
        onClick={onReset}
        className="inline-flex items-center gap-1 h-8 px-2 rounded-md text-meta text-ink-secondary hover:bg-inset"
        data-testid="audit-reset-filters"
      >
        <RefreshCw size={11} /> Reset
      </button>
      <span className="text-meta text-ink-secondary tabular-nums" data-testid="audit-match-count">
        {matchCount} / {totalCount}
      </span>
      {csvExport}
    </div>
  );
}

function Select({ value, onChange, testId, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="h-8 px-2 text-sm rounded-md border border-border-subtle bg-white focus:outline-none focus:border-tralenex-400"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function EventTable({ items, onSelect, onTarget }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-inset/40 border-b border-border-subtle">
          <tr>
            {["Time", "Actor", "Activity", "Entity", "Target", "Severity", "Hash", ""].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-meta font-semibold tracking-[0.04em] uppercase text-ink-secondary">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {items.map((e) => (
            <EventRow key={e.id} entry={e} onSelect={onSelect} onTarget={onTarget} />
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={8} className="px-6 py-10 text-center text-ink-secondary">
                No audit events match the current filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function EventRow({ entry, onSelect, onTarget }) {
  const linkable = !!resolveAuditTargetRoute(entry);
  const handleTargetClick = useCallback(
    (e) => {
      e.stopPropagation();
      onTarget(entry);
    },
    [entry, onTarget],
  );
  return (
    <tr
      onClick={() => onSelect(entry)}
      className="hover:bg-inset/40 cursor-pointer transition-colors"
      data-testid={`audit-row-${entry.id}`}
    >
      <td className="px-3 py-2 text-meta font-mono text-ink-secondary tabular-nums whitespace-nowrap">{entry.ts}</td>
      <td className="px-3 py-2 text-ink-primary whitespace-nowrap">{entry.actor}</td>
      <td className="px-3 py-2 text-ink-primary text-sm max-w-[360px]">
        {entry.narrative ? (
          <span className="block leading-snug">{entry.narrative}</span>
        ) : looksLikeSentence(entry.action) ? (
          <span className="block leading-snug">{entry.action}</span>
        ) : (
          <span className="font-mono text-meta">{entry.action}</span>
        )}
      </td>
      <td className="px-3 py-2"><EntityChip type={entry.entityType} /></td>
      <td className="px-3 py-2">
        {linkable ? (
          <button
            type="button"
            onClick={handleTargetClick}
            className="inline-flex items-center gap-1 text-tralenex-700 hover:underline font-mono text-meta tabular-nums"
            data-testid={`audit-target-link-${entry.id}`}
          >
            {entry.entityRef} <ArrowUpRight size={11} />
          </button>
        ) : (
          <span className="font-mono text-meta tabular-nums text-ink-secondary">{entry.entityRef}</span>
        )}
      </td>
      <td className="px-3 py-2"><SeverityPill severity={entry.severity} /></td>
      <td className="px-3 py-2"><HashBadge value={entry.hash} testId={`audit-hash-${entry.id}`} /></td>
      <td className="px-3 py-2 text-ink-disabled"><ArrowUpRight size={14} /></td>
    </tr>
  );
}

function CustomerEmpty({ userName, onSubmit }) {
  return (
    <div className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden anim-fade-up" data-testid="audit-customer-empty">
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>
      <div className="relative max-w-3xl">
        <p className="eyebrow">Settings · Audit Log</p>
        <h1 className="text-display-lg text-ink-primary mt-1">
          {userName ? `Welcome, ${userName}.` : "Welcome."}
        </h1>
        <p className="text-ink-secondary mt-3 max-w-xl text-[15px]">
          The audit ledger writes its first entry the moment you connect an
          AI agent. From that point, every approval, policy publish and
          drift signal is hash-chained — forever auditable, never editable.
        </p>
        <button
          type="button"
          onClick={onSubmit}
          className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700"
          data-testid="audit-empty-cta"
        >
          <Plus size={14} /> Set up agent inventory
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function filterEntry(e, { q, entity, severity, actor, date }) {
  if (entity && e.entityType !== entity) return false;
  if (severity && e.severity !== severity) return false;
  if (actor && e.actor !== actor) return false;
  if (date && !withinDateWindow(e.ts, date)) return false;
  if (q) {
    const hay = `${e.action} ${e.narrative || ""} ${e.actor} ${e.entityRef} ${e.hash}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

// Demo timestamps look like "14 Mar 2026 09:12 AEDT". We anchor "now" to the
// freshest entry so the demo "Last 24h" filter always returns something.
const MONTH = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
const NOW_MS = new Date(2026, MONTH.Mar, 14, 10, 0, 0).getTime();

function parseTs(ts) {
  // "14 Mar 2026 09:12 AEDT" → epoch ms (AEDT is UTC+11; we ignore tz for demo).
  const m = ts.match(/^(\d{1,2}) (\w{3}) (\d{4}) (\d{2}):(\d{2})/);
  if (!m) return NaN;
  const [, d, mon, y, h, min] = m;
  return new Date(Number(y), MONTH[mon] ?? 0, Number(d), Number(h), Number(min)).getTime();
}

function withinDateWindow(ts, key) {
  const t = parseTs(ts);
  if (Number.isNaN(t)) return true;
  const ms = { "24h": 24 * 3600e3, "7d": 7 * 86400e3, "14d": 14 * 86400e3 }[key];
  if (!ms) return true;
  return NOW_MS - t <= ms;
}

function deriveSummary(entries) {
  if (!entries.length) {
    return { total: 0, last24h: 0, actorCount: 0, systemEvents: 0, high: 0 };
  }
  const last24h = entries.filter((e) => withinDateWindow(e.ts, "24h")).length;
  const actorSet = new Set(entries.map((e) => e.actor));
  const systemEvents = entries.filter((e) => e.actor === "system").length;
  const high = entries.filter((e) => e.severity === "high").length;
  return { total: entries.length, last24h, actorCount: actorSet.size, systemEvents, high };
}


// ───────────────────────────────────────────────────────────────────────────
// Chain KPI helpers
// ───────────────────────────────────────────────────────────────────────────
function chainKpiValue(status) {
  if (status === "verified") return "\u2713";
  if (status === "broken")   return "\u2716";
  if (status === "error")    return "!";
  if (status === "verifying") return "\u2026";
  return "?";
}
function chainKpiDelta(status) {
  if (status === "verified") return "verified";
  if (status === "broken")   return "chain broken";
  if (status === "error")    return "verify failed";
  if (status === "verifying") return "verifying…";
  return "not verified yet";
}
function chainKpiTone(status) {
  if (status === "verified") return "up";
  if (status === "broken" || status === "error") return "down";
  return "neutral";
}

// ───────────────────────────────────────────────────────────────────────────
// VerifyResultModal — renders the structured backend verification result.
// ───────────────────────────────────────────────────────────────────────────
function VerifyResultModal({ result, error, onClose }) {
  if (!result && !error) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      data-testid="audit-verify-modal"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink-primary/40 backdrop-blur-[2px]"
        data-testid="audit-verify-backdrop"
      />
      <div className="relative w-full max-w-lg surface-card shadow-e4 overflow-hidden anim-pop">
        {error ? (
          <VerifyErrorBody error={error} onClose={onClose} />
        ) : (
          <VerifyResultBody result={result} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

function VerifyResultBody({ result, onClose }) {
  const ok = result.valid;
  return (
    <>
      <div
        className={cn(
          "px-5 pt-5 pb-3 border-b border-border-subtle flex items-start gap-3",
          ok ? "bg-risk-low/5" : "bg-risk-high/5",
        )}
      >
        <span className={ok ? "text-risk-low" : "text-risk-high"}>
          {ok ? <CheckCircle2 size={22} /> : <AlertTriangle size={22} />}
        </span>
        <div className="flex-1 min-w-0">
          <p className="eyebrow">Audit chain · verification result</p>
          <h3 className="mt-0.5 text-h2 text-ink-primary" data-testid="audit-verify-headline">
            {ok ? "Chain verified" : "Chain integrity broken"}
          </h3>
          <p className="mt-1 text-meta text-ink-secondary">
            {ok
              ? `${result.totalEntries} entries linked cleanly from genesis to head.`
              : "A `prevHash` link does not match the expected predecessor — see details below."}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-ink-secondary hover:bg-inset"
          aria-label="Close"
          data-testid="audit-verify-close"
        >
          <X size={16} />
        </button>
      </div>

      <div className="px-5 py-4 grid grid-cols-2 gap-3 text-sm">
        <Stat label="Entries" value={result.totalEntries} testId="audit-verify-total" />
        <Stat
          label="Link integrity"
          value={result.linkIntegrity.ok ? "OK" : "Broken"}
          tone={result.linkIntegrity.ok ? "good" : "bad"}
          testId="audit-verify-link"
        />
        <Stat
          label="Sealed (recompute matches)"
          value={result.contentIntegrity.sealed}
          testId="audit-verify-sealed"
        />
        <Stat
          label="Unsealed (legacy/seed)"
          value={result.contentIntegrity.unsealed}
          testId="audit-verify-unsealed"
        />
      </div>

      {result.linkIntegrity.brokenAt && (
        <div className="px-5 pb-4">
          <p className="eyebrow text-risk-high">First broken link</p>
          <div className="mt-1 p-3 rounded-md border border-risk-high/30 bg-risk-high/5 text-meta text-ink-primary font-mono" data-testid="audit-verify-broken">
            <div>Entry: {result.linkIntegrity.brokenAt.id}</div>
            <div>Expected prevHash: {result.linkIntegrity.brokenAt.expectedPrevHash}</div>
            <div>Actual prevHash: &nbsp;&nbsp;{result.linkIntegrity.brokenAt.actualPrevHash}</div>
          </div>
        </div>
      )}

      <div className="px-5 pb-4">
        <p className="eyebrow">Endpoints</p>
        <div className="mt-1 grid grid-cols-1 gap-1.5 text-meta font-mono text-ink-secondary">
          {result.genesis && (
            <div>genesis &nbsp;{result.genesis.prevHash} &nbsp;→ &nbsp;{result.genesis.id}</div>
          )}
          {result.head && (
            <div>head &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{result.head.id} &nbsp;→ &nbsp;{result.head.hash}</div>
          )}
        </div>
      </div>

      {result.auditEntryId && (
        <div className="px-5 pb-4">
          <p className="eyebrow">Self-audit breadcrumb</p>
          <p className="mt-1 text-meta text-ink-secondary leading-snug">
            This verification was itself recorded as audit entry{" "}
            <span className="font-mono text-ink-primary" data-testid="audit-verify-self-audit-id">
              {result.auditEntryId}
            </span>
            . Every press of <em>Verify chain</em> leaves a permanent breadcrumb so
            future audits can prove this check was performed.
          </p>
        </div>
      )}

      <div className="px-5 py-3 border-t border-border-subtle bg-inset/30 flex items-center justify-between gap-2">
        <span className="text-meta text-ink-disabled font-mono tabular-nums">
          verifiedAt {result.verifiedAt}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="h-8 px-3 rounded-md text-sm font-semibold bg-tralenex-600 text-white hover:bg-tralenex-700"
          data-testid="audit-verify-dismiss"
        >
          Close
        </button>
      </div>
    </>
  );
}

function VerifyErrorBody({ error, onClose }) {
  return (
    <>
      <div className="px-5 pt-5 pb-3 border-b border-border-subtle flex items-start gap-3 bg-risk-high/5">
        <span className="text-risk-high"><AlertTriangle size={22} /></span>
        <div className="flex-1 min-w-0">
          <p className="eyebrow">Audit chain · verification failed</p>
          <h3 className="mt-0.5 text-h2 text-ink-primary" data-testid="audit-verify-error-headline">Verification could not run</h3>
          <p className="mt-1 text-meta text-ink-secondary" data-testid="audit-verify-error">{error}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-ink-secondary hover:bg-inset"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>
      <div className="px-5 py-4 text-sm text-ink-secondary">
        Confirm you have the <code className="font-mono">verify:audit-chain</code> permission
        for this tenant and try again.
      </div>
    </>
  );
}

function Stat({ label, value, tone, testId }) {
  const toneCls = tone === "good"
    ? "text-risk-low"
    : tone === "bad"
      ? "text-risk-high"
      : "text-ink-primary";
  return (
    <div className="rounded-md border border-border-subtle p-3">
      <p className="eyebrow">{label}</p>
      <p className={cn("mt-1 text-h3 tabular-nums font-semibold", toneCls)} data-testid={testId}>
        {value}
      </p>
    </div>
  );
}
